/**
 * CSV import and export.
 *
 * Import is header-driven: real spreadsheets from the sales team have wildly
 * inconsistent column names, so HEADER_MAP normalises whatever they send onto
 * the ledger's field keys. Rows whose email already exists update in place
 * rather than creating a duplicate client.
 */
import { FIELDS } from '../constants/defaults.js';
import { formatDate } from './dates.js';
import { download } from './format.js';

/** RFC-4180-ish parser: handles quotes, escaped quotes, tabs and CRLF. */
export function parseCSV(text) {
  const out = [];
  let row = [];
  let cell = '';
  let quoted = false;
  const t = String(text).replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');

  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (quoted) {
      if (ch === '"') {
        if (t[i + 1] === '"') { cell += '"'; i++; } else quoted = false;
      } else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',' || ch === '\t') { row.push(cell); cell = ''; }
    else if (ch === '\n') { row.push(cell); out.push(row); row = []; cell = ''; }
    else cell += ch;
  }
  if (cell.length || row.length) { row.push(cell); out.push(row); }
  return out.filter(r => r.some(c => String(c).trim()));
}

const normalise = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const HEADER_MAP = {
  sno: 'sno', sn: 'sno', srno: 'sno', no: 'sno',
  customerclientnameusername: 'name', customerclientname: 'name', clientname: 'name',
  customername: 'name', username: 'name', name: 'name', client: 'name', customer: 'name',
  email: 'email', emailaddress: 'email', mail: 'email',
  accounttype: 'acctType', type: 'acctType', accttype: 'acctType',
  registeredon: 'regOn', registered: 'regOn', registrationdate: 'regOn',
  signupdate: 'regOn', createdon: 'regOn',
  regsource: 'source', registrationsource: 'source', source: 'source',
  currentplan: 'plan', plan: 'plan', subscription: 'plan',
  trialend: 'trialEnd', trailend: 'trialEnd', trialenddate: 'trialEnd',
  expirydate: 'expiry', expiry: 'expiry', enddate: 'expiry', renewaldate: 'expiry',
  cycle: 'cycle', billingcycle: 'cycle',
  promocode: 'promo', promo: 'promo', coupon: 'promo', couponcode: 'promo',
  paymentstatus: 'pay', payment: 'pay', paid: 'pay',
  status: 'status', accountstatus: 'status',
  handledby: 'handler', owner: 'handler', accountmanager: 'handler', assignedto: 'handler',
  contactperson: 'contact', contact: 'contact', contactname: 'contact',
  phone: 'phone', mobile: 'phone', whatsapp: 'phone', contactnumber: 'phone', phonenumber: 'phone',
  addons: 'addons', addon: 'addons', modules: 'addons', addonproducts: 'addons', extras: 'addons',
  amount: 'amount', paymentamount: 'amount', price: 'amount', value: 'amount', fee: 'amount',
  nextdue: 'nextDue', nextduedate: 'nextDue', nextpayment: 'nextDue', nextbilling: 'nextDue',
  lastpaid: 'lastPaid', lastpayment: 'lastPaid', lastpaiddate: 'lastPaid',
  lastactivity: 'lastActivity', lastlogin: 'lastActivity', lastseen: 'lastActivity',
  lastcontacted: 'lastContacted', lastcontact: 'lastContacted', lastfollowup: 'lastContacted',
  notes: 'notes', note: 'notes', remarks: 'notes', comments: 'notes'
};

export const mapHeader = h => {
  const n = normalise(h);
  if (HEADER_MAP[n]) return HEADER_MAP[n];
  // "14 Day Trial End", "14 Days Trial End", "30-day trail end", …
  if (/^\d+days?tr[ai]+lend$/.test(n)) return 'trialEnd';
  return null;
};

/**
 * Turn raw CSV text into records. Returns `{ error }` when the file cannot be
 * used, so the caller decides how to tell the user.
 */
export function readClientCSV(text) {
  const grid = parseCSV(text);
  if (grid.length < 2) return { error: 'That file has no data rows' };

  // The header is not always row 0 — spreadsheets often carry a title block.
  let headerRow = 0;
  for (let i = 0; i < Math.min(grid.length, 10); i++) {
    if (grid[i].some(c => ['email', 'emailaddress'].includes(normalise(c)))) { headerRow = i; break; }
  }

  const map = grid[headerRow].map(mapHeader);
  if (!map.some(Boolean)) return { error: 'Column names not recognised — check the header row' };

  const records = [];
  for (let i = headerRow + 1; i < grid.length; i++) {
    const rec = {};
    map.forEach((k, j) => { if (k) rec[k] = String(grid[i][j] || '').trim(); });
    if (!rec.name && !rec.email) continue;
    records.push(rec);
  }
  if (!records.length) return { error: 'No rows with a name or email found' };
  return { records };
}

/*
 * One cell, quoted for CSV.
 *
 * A text value that starts with = + - @ (or a tab or carriage return) is run
 * as a formula when the file is opened in Excel or Sheets, and the names and
 * notes here come from public sign-up forms — so such a value gets an
 * apostrophe in front and stays text. Real numbers are left alone, so a
 * negative amount is still a number.
 */
const quote = v => {
  let s = String(v == null ? '' : v);
  if (typeof v !== 'number' && /^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
};

export function exportClientsCSV(list, deriveType, dateFmt = 'long') {
  const fields = FIELDS.filter(f => f.t !== 'derived');
  /*
   * Two the loop above cannot take.
   *
   * `derived` means "no plain value on the row" — a progress bar, a risk
   * score — and those are rightly skipped. The partner is marked derived only
   * because it must not be editable here: the clients app owns it. But it is
   * a plain string, and it is the whole point of half the exports somebody
   * takes off this screen, so it is named rather than dropped.
   */
  const extra = [
    ['Partner', r => r.partner],
    ['Partner promo code', r => r.partnerPromo],
    /* Which of the partner's three this customer actually arrived on — the
       one fact in here that is about them rather than about the partner. */
    ['Signed up on', r => r.partnerVia]
  ];
  const header = [...fields.map(f => quote(f.h)), ...extra.map(([h]) => quote(h))].join(',');
  const body = list.map(r => [
    ...fields.map(f => quote(f.k === 'acctType' ? (r.acctType || deriveType(r)) : r[f.k])),
    ...extra.map(([, read]) => quote(read(r)))
  ].join(','));
  const csv = `\uFEFF${[header, ...body].join('\r\n')}`;
  const stamp = formatDate(new Date(), false, dateFmt).replace(/ /g, '-');
  download(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `client-ledger-${stamp}.csv`);
  return list.length;
}

/* ------------------------------------------------------------ pipeline */

/**
 * The board as a spreadsheet - the columns the board shows, nothing invented.
 * `details` is leadDetails from the domain, handed in so this file stays free
 * of the CRM helpers.
 */
export function exportPipelineCSV(list, { companyName, details }, dateFmt = 'long') {
  const cols = [
    ['Name', d => d.title],
    ['Email', (d, f) => f.email],
    ['Phone', (d, f) => f.phone],
    ['Company', d => companyName(d.cid)],
    ['City', (d, f) => f.city],
    ['Country', (d, f) => f.country],
    ['Stage', d => d.stage],
    ['Source', d => d.source],
    ['Campaign', (d, f) => f.campaign],
    ['Wants', (d, f) => f.products],
    ['Requested', (d, f) => f.requested],
    ['Landed on', (d, f) => f.landing],
    ['Owner', d => d.owner],
    ['Notes', d => d.notes]
  ];
  const header = cols.map(c => quote(c[0])).join(',');
  const body = list.map(d => {
    const f = details(d);
    return cols.map(c => quote(c[1](d, f))).join(',');
  });
  const csv = `\uFEFF${[header, ...body].join('\r\n')}`;
  const stamp = formatDate(new Date(), false, dateFmt).replace(/ /g, '-');
  download(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `pipeline-${stamp}.csv`);
  return list.length;
}

/** The Deals table as a spreadsheet: the pipeline's columns plus what a
    deal adds - value, plan, the day it was won. */
export function exportDealsCSV(list, { companyName, details }, dateFmt = 'long') {
  const cols = [
    ['Name', d => d.title],
    ['Email', (d, f) => f.email],
    ['Phone', (d, f) => f.phone],
    ['Company', d => companyName(d.cid)],
    ['City', (d, f) => f.city],
    ['Country', (d, f) => f.country],
    ['Source', d => d.source],
    ['Campaign', (d, f) => f.campaign],
    ['Plan', d => d.plan],
    ['Cycle', d => d.cycle],
    ['Value', d => d.value],
    ['Won on', d => d.close],
    ['Requested', (d, f) => f.requested],
    ['Landed on', (d, f) => f.landing],
    ['Owner', d => d.owner],
    ['Notes', d => d.notes]
  ];
  const header = cols.map(c => quote(c[0])).join(',');
  const body = list.map(d => {
    const f = details(d);
    return cols.map(c => quote(c[1](d, f))).join(',');
  });
  const csv = `\uFEFF${[header, ...body].join('\r\n')}`;
  const stamp = formatDate(new Date(), false, dateFmt).replace(/ /g, '-');
  download(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `deals-${stamp}.csv`);
  return list.length;
}

/**
 * Rows to add to the pipeline by hand, in bulk. Header-driven like the client
 * import: Name is the only column that has to be there.
 */
export function readPipelineCSV(text) {
  const grid = parseCSV(text).filter(r => r.some(c => String(c).trim()));
  if (grid.length < 2) return { error: 'The file has no rows under its header' };

  const norm = h => String(h).toLowerCase().replace(/[^a-z]/g, '');
  const KEYS = {
    name: ['name', 'lead', 'contact', 'person', 'request'],
    email: ['email', 'mail'],
    phone: ['phone', 'mobile', 'tel', 'whatsapp'],
    company: ['company', 'organisation', 'organization', 'business'],
    stage: ['stage', 'status'],
    plan: ['wants', 'plan', 'product', 'products', 'interestedin'],
    notes: ['notes', 'note', 'comments', 'remarks']
  };
  const header = grid[0].map(norm);
  const at = key => header.findIndex(h => KEYS[key].includes(h));
  const idx = Object.fromEntries(Object.keys(KEYS).map(k => [k, at(k)]));
  if (idx.name < 0 && idx.email < 0) return { error: 'No Name or Email column found' };

  const cell = (row, k) => (idx[k] >= 0 ? String(row[idx[k]] || '').trim() : '');
  const records = grid.slice(1)
    .map(row => ({
      title: cell(row, 'name') || cell(row, 'email'),
      email: cell(row, 'email'),
      phone: cell(row, 'phone'),
      company: cell(row, 'company'),
      stage: cell(row, 'stage'),
      plan: cell(row, 'plan'),
      notes: cell(row, 'notes')
    }))
    .filter(r => r.title);
  if (!records.length) return { error: 'No rows with a name or email found' };
  return { records };
}

export function exportBackup({ settings, clients, crm }, dateFmt = 'long') {
  const payload = JSON.stringify(
    { version: 3, exported: new Date().toISOString(), settings, clients, crm }, null, 2);
  const stamp = formatDate(new Date(), false, dateFmt).replace(/ /g, '-');
  download(new Blob([payload], { type: 'application/json' }), `client-ledger-backup-${stamp}.json`);
}
